import { useCallback, useEffect, useState } from 'react'

/**
 * useState backed by localStorage for per-user UI preferences (density,
 * toggles). Storage can be unavailable (private mode, quota, SSR/tests), so
 * every access is guarded and the hook silently falls back to memory.
 */
export function usePersistentState<T>(key: string, initial: T, isValid?: (value: unknown) => value is T) {
  const read = (): T => {
    try {
      const raw = window.localStorage.getItem(key)
      if (raw === null) return initial
      const parsed: unknown = JSON.parse(raw)
      if (isValid && !isValid(parsed)) return initial
      return parsed as T
    } catch {
      return initial
    }
  }
  const [state, setState] = useState<T>(read)

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(state))
    } catch {
      // Preference persistence is best-effort.
    }
  }, [key, state])

  const update = useCallback((next: T | ((previous: T) => T)) => setState(next), [])
  return [state, update] as const
}
